import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Alert,
  Spinner,
} from 'reactstrap';
import classnames from 'classnames';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { authUserManager } from '../../../helpers/api_helper';
import { getUserProfile, updateUserProfile, changePassword, resetProfileFlags } from '../../../slices/profile/thunk';
import { validators } from '../../../helpers/ValidatorHelper';

import avatar1 from '../../../assets/images/users/user-dummy-img.jpg';
import {appName} from "../../../Components/constants/default";

const ProfileSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventId = authUserManager.getAdminEventId();
  const userId = authUserManager.getId();
  const [activeTab, setActiveTab] = useState('1');
  const [passwordShow, setPasswordShow] = useState({ old: false, new: false, confirm: false });

  // Redux selector
  const selectProfileState = (state) => state.AdminProfile;
  const profileSelector = createSelector(selectProfileState, (profile) => ({
    user: profile.user,
    loading: profile.loading,
    updateSuccess: profile.updateSuccess,
    passwordChangeSuccess: profile.passwordChangeSuccess,
    changePasswordViolations: profile.changePasswordViolations,
    updateUserProfileViolations: profile.updateUserProfileViolations,
  }));

  const { user, loading, updateSuccess, passwordChangeSuccess, changePasswordViolations, updateUserProfileViolations } = useSelector(profileSelector);

  // Pobierz profil przy montowaniu
  useEffect(() => {
    dispatch(getUserProfile(userId));
  }, [dispatch]);

  // Reset flags po 3 sekundach
  useEffect(() => {
    if (updateSuccess || passwordChangeSuccess) {
      const timer = setTimeout(() => {
        dispatch(resetProfileFlags());
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [updateSuccess, passwordChangeSuccess, dispatch]);

  const tabChange = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Formik - Personal Details
  const profileValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: user?.email || '',
      username: user?.username || '',
      facebookLink: user?.facebookLink || '',
      instagramLink: user?.instagramLink || '',
      websiteLink: user?.websiteLink || '',
      youtubeLink: user?.youtubeLink || '',
      tiktokLink: user?.tiktokLink || '',
    },
    validationSchema: Yup.object({
      email: validators.email(t),
      username: validators.usernameOptional(t),
      facebookLink: Yup.string(),
      instagramLink: Yup.string(),
      websiteLink: Yup.string(),
      youtubeLink: Yup.string(),
      tiktokLink: Yup.string(),
    }),
    onSubmit: (values) => {
      dispatch(updateUserProfile(user.id, values));
    },
  });

  useEffect(() => {
    if (updateUserProfileViolations?.length > 0) {
      updateUserProfileViolations.forEach((violation) => {
        updateUserProfileViolations.setFieldError(violation.propertyPath, t(violation.message));
      });
    }
  }, [updateUserProfileViolations]);

  useEffect(() => {
    if (changePasswordViolations?.length > 0) {
      changePasswordViolations.forEach((violation) => {
        passwordValidation.setFieldError(violation.propertyPath, t(violation.message));
      });
    }
  }, [changePasswordViolations]);

  // Formik - Change Password
  const passwordValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required(t('Old password is required')),
      newPassword: validators.password(t),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], t('Passwords do not match'))
        .required(t('Please confirm your password')),
    }),
    onSubmit: (values) => {
      dispatch(changePassword(user.id, values));
      // passwordValidation.resetForm();
    },
  });


  document.title = t('Profile Settings') + " | " + appName;

  if (!user || loading) {
    return (
      <div className="page-content non-admin-layout">
        <Container fluid>
          <div className="text-center mt-5">
            <Spinner color="primary" />
            <p className="mt-3">{t('Loading profile...')}</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content non-admin-layout">
        <Container fluid>
          <BreadCrumb title={t('Profile')} pageTitle={t('Event View')} link={'/admin/event/' + eventId} />
          <Row>
            {/* Sidebar */}
            <Col xxl={3}>
              <Card>
                <CardBody className="p-4">
                  <div className="text-center">
                    <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                      <img
                        src={avatar1}
                        className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                        alt="user-profile"
                      />
                    </div>
                    {user.username && <h5 className="fs-16 mb-1">{user.username}</h5>}
                    <p className="text-muted">{user.email}</p>
                  </div>
                </CardBody>
              </Card>

              {/* Social Media Card */}
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center mb-4">
                    <div className="flex-grow-1">
                      <h5 className="card-title mb-0">{t('Social Media')}</h5>
                    </div>
                  </div>
                  <div className="mb-3 d-flex">
                    <div className="avatar-xs d-block flex-shrink-0 me-3">
                      <span className="avatar-title rounded-circle fs-16 bg-primary">
                        <i className="ri-facebook-fill"></i>
                      </span>
                    </div>
                    <Input
                      type="text"
                      className="form-control"
                      name="facebookLink"
                      placeholder={t('Facebook link')}
                      value={profileValidation.values.facebookLink}
                      onChange={profileValidation.handleChange}
                    />
                  </div>
                  <div className="mb-3 d-flex">
                    <div className="avatar-xs d-block flex-shrink-0 me-3">
                      <span className="avatar-title rounded-circle fs-16 bg-pink">
                        <i className="ri-instagram-fill"></i>
                      </span>
                    </div>
                    <Input
                      type="text"
                      className="form-control"
                      name="instagramLink"
                      placeholder={t('Instagram link')}
                      value={profileValidation.values.instagramLink}
                      onChange={profileValidation.handleChange}
                    />
                  </div>
                  <div className="mb-3 d-flex">
                    <div className="avatar-xs d-block flex-shrink-0 me-3">
                      <span className="avatar-title rounded-circle fs-16 bg-black">
                        <i className="ri-tiktok-fill"></i>
                      </span>
                    </div>
                    <Input
                      type="text"
                      className="form-control"
                      name="tiktokLink"
                      placeholder={t('TikTok link')}
                      value={profileValidation.values.tiktokLink}
                      onChange={profileValidation.handleChange}
                    />
                  </div>
                  <div className="mb-3 d-flex">
                    <div className="avatar-xs d-block flex-shrink-0 me-3">
                      <span className="avatar-title rounded-circle fs-16 bg-danger">
                        <i className="ri-youtube-fill"></i>
                      </span>
                    </div>
                    <Input
                      type="text"
                      className="form-control"
                      name="youtubeLink"
                      placeholder={t('YouTube link')}
                      value={profileValidation.values.youtubeLink}
                      onChange={profileValidation.handleChange}
                    />
                  </div>
                  <div className="d-flex">
                    <div className="avatar-xs d-block flex-shrink-0 me-3">
                      <span className="avatar-title rounded-circle fs-16 bg-info">
                        <i className="ri-global-line"></i>
                      </span>
                    </div>
                    <Input
                      type="text"
                      className="form-control"
                      name="websiteLink"
                      placeholder={t('Website link')}
                      value={profileValidation.values.websiteLink}
                      onChange={profileValidation.handleChange}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Main Content */}
            <Col xxl={9}>
              <Card className="mt-xxl-n5">
                <CardHeader>
                  <Nav className="nav-tabs-custom rounded card-header-tabs border-bottom-0 nav-success" role="tablist">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => tabChange('1')}
                        type="button"
                      >
                        <i className="fas fa-home"></i>
                        {t('Personal Details')}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => tabChange('2')}
                        type="button"
                      >
                        <i className="far fa-user"></i>
                        {t('Password')}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>
                <CardBody className="p-4">
                  <TabContent activeTab={activeTab}>
                    {/* Personal Details Tab */}
                    <TabPane tabId="1">
                      {updateSuccess && (
                        <Alert color="success" className="mb-3">
                          <i className="ri-checkbox-circle-line me-2"></i>
                          {t('Profile updated successfully!')}
                        </Alert>
                      )}
                      {updateUserProfileViolations && activeTab === '1' && (
                        <Alert color="danger" className="mb-3">
                          {t('Failed to change profile settings')}
                        </Alert>
                      )}
                      <Form onSubmit={profileValidation.handleSubmit}>
                        <Row>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label htmlFor="username" className="form-label">
                                {t('Username')}
                              </Label>
                              <Input
                                type="text"
                                className="form-control"
                                id="username"
                                name="username"
                                placeholder={t('Enter your username')}
                                value={profileValidation.values.username}
                                onChange={profileValidation.handleChange}
                                onBlur={profileValidation.handleBlur}
                                invalid={
                                  profileValidation.touched.username && !!profileValidation.errors.username
                                }
                              />
                              {profileValidation.touched.username && profileValidation.errors.username && (
                                <div className="invalid-feedback d-block">
                                  {profileValidation.errors.username}
                                </div>
                              )}
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label htmlFor="email" className="form-label">
                                {t('Email Address')} <span className="text-danger">*</span>
                              </Label>
                              <Input
                                type="email"
                                className="form-control"
                                id="email"
                                name="email"
                                disabled
                                placeholder={t('Enter your email')}
                                value={profileValidation.values.email}
                                onChange={profileValidation.handleChange}
                                onBlur={profileValidation.handleBlur}
                                invalid={
                                  profileValidation.touched.email && !!profileValidation.errors.email
                                }
                              />
                              {profileValidation.touched.email && profileValidation.errors.email && (
                                <div className="invalid-feedback d-block">
                                  {profileValidation.errors.email}
                                </div>
                              )}
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div className="hstack gap-2 justify-content-end">
                              <button type="submit" className="btn btn-success" disabled={loading}>
                                {loading && <Spinner size="sm" className="me-2" />}
                                {t('Update')}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </TabPane>

                    {/* Change Password Tab */}
                    <TabPane tabId="2">
                      {passwordChangeSuccess && (
                        <Alert color="success" className="mb-3">
                          <i className="ri-checkbox-circle-line me-2"></i>
                          {t('Password changed successfully!')}
                        </Alert>
                      )}
                      {changePasswordViolations && activeTab === '2' && (
                        <Alert color="danger" className="mb-3">
                          {t('Failed to change password')}
                        </Alert>
                      )}
                      <Form onSubmit={passwordValidation.handleSubmit}>
                        <Row className="g-2">
                          <Col lg={12}>
                            <div className="mb-3">
                              <Label htmlFor="oldPassword" className="form-label">
                                {t('Old Password')} <span className="text-danger">*</span>
                              </Label>
                              <div className="position-relative auth-pass-inputgroup">
                                <Input
                                  type={passwordShow.old ? 'text' : 'password'}
                                  className={`form-control ${passwordValidation.touched.oldPassword && passwordValidation.errors.oldPassword ? 'is-invalid' : ''}`}
                                  id="oldPassword"
                                  name="oldPassword"
                                  placeholder={t('Enter current password')}
                                  value={passwordValidation.values.oldPassword}
                                  onChange={passwordValidation.handleChange}
                                  onBlur={passwordValidation.handleBlur}
                                  invalid={
                                    passwordValidation.touched.oldPassword &&
                                    !!passwordValidation.errors.oldPassword
                                  }
                                />
                                {passwordValidation.touched.oldPassword &&
                                  passwordValidation.errors.oldPassword && (
                                    <div className="invalid-feedback d-block">
                                      {passwordValidation.errors.oldPassword}
                                    </div>
                                  )}
                                <button
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  type="button"
                                  onClick={() =>
                                    setPasswordShow({ ...passwordShow, old: !passwordShow.old })
                                  }
                                >
                                  <i className="ri-eye-fill align-middle"></i>
                                </button>
                              </div>
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <Label htmlFor="newPassword" className="form-label">
                                {t('New Password')} <span className="text-danger">*</span>
                              </Label>
                              <div className="position-relative auth-pass-inputgroup">
                                <Input
                                  type={passwordShow.new ? 'text' : 'password'}
                                  className="form-control"
                                  id="newPassword"
                                  name="newPassword"
                                  placeholder={t('Enter new password')}
                                  value={passwordValidation.values.newPassword}
                                  onChange={passwordValidation.handleChange}
                                  onBlur={passwordValidation.handleBlur}
                                  invalid={
                                    passwordValidation.touched.newPassword &&
                                    !!passwordValidation.errors.newPassword
                                  }
                                />
                                {passwordValidation.touched.newPassword &&
                                  passwordValidation.errors.newPassword && (
                                    <div className="invalid-feedback d-block">
                                      {passwordValidation.errors.newPassword}
                                    </div>
                                  )}
                                <button
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  type="button"
                                  onClick={() =>
                                    setPasswordShow({ ...passwordShow, new: !passwordShow.new })
                                  }
                                >
                                  <i className="ri-eye-fill align-middle"></i>
                                </button>
                              </div>
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <Label htmlFor="confirmPassword" className="form-label">
                                {t('Confirm Password')} <span className="text-danger">*</span>
                              </Label>
                              <div className="position-relative auth-pass-inputgroup">
                                <Input
                                  type={passwordShow.confirm ? 'text' : 'password'}
                                  className="form-control"
                                  id="confirmPassword"
                                  name="confirmPassword"
                                  placeholder={t('Confirm password')}
                                  value={passwordValidation.values.confirmPassword}
                                  onChange={passwordValidation.handleChange}
                                  onBlur={passwordValidation.handleBlur}
                                  invalid={
                                    passwordValidation.touched.confirmPassword &&
                                    !!passwordValidation.errors.confirmPassword
                                  }
                                />
                                {passwordValidation.touched.confirmPassword &&
                                  passwordValidation.errors.confirmPassword && (
                                    <div className="invalid-feedback d-block">
                                      {passwordValidation.errors.confirmPassword}
                                    </div>
                                  )}
                                <button
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  type="button"
                                  onClick={() =>
                                    setPasswordShow({ ...passwordShow, confirm: !passwordShow.confirm })
                                  }
                                >
                                  <i className="ri-eye-fill align-middle"></i>
                                </button>
                              </div>
                            </div>
                          </Col>

                          <Col lg={12}>
                            <div className="text-end">
                              <button type="submit" className="btn btn-success" disabled={loading}>
                                {loading && <Spinner size="sm" className="me-2" />}
                                {t('Change Password')}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProfileSettings;
